import * as React from 'react';

import { useTranslation } from 'react-i18next';

import { notifyAlert } from '@pricemoov/pollux-react';

import useAppVersionQuery from 'redesign/apiREST/queries/useAppVersionQuery';

export interface AppVersionContextProps {
    appVersion: string;
    isOutdatedVersion: boolean;
}

export const AppVersionContext = React.createContext<AppVersionContextProps>({
    appVersion: undefined,
    isOutdatedVersion: false,
});

let VERSION_POLLING_INTERVAL = 1000 * 60 * 10;

if (process.env.NODE_ENV === 'test') {
    VERSION_POLLING_INTERVAL = 1000;
}

const AppVersionManager = ({ children }: { children: JSX.Element }) => {
    const { t } = useTranslation();
    const [isOutdatedVersion, setIsOutdatedVersion] = React.useState(false);
    const [lastVersion, setLastVersion] = React.useState<string>(null);
    const [{ data: version }] = useAppVersionQuery({
        onSuccess: newAppVersion => {
            if (lastVersion !== newAppVersion.Id && newAppVersion.Status === 'Completed') {
                if (lastVersion) {
                    notifyAlert(
                        {
                            level: 'warning',
                            title: (
                                <div>
                                    {t('general.app_version_outdated.message')}
                                    <div
                                        role="button"
                                        className="polx-text-warning"
                                        tabIndex={0}
                                        onClick={() => {
                                            window.location.reload();
                                        }}
                                    >
                                        {t('general.app_version_outdated.retry')}
                                    </div>
                                </div>
                            ),
                        },
                        { autoClose: false }
                    );
                    setIsOutdatedVersion(true);
                }
                setLastVersion(newAppVersion.Id);
            }
        },
        refetchInterval: VERSION_POLLING_INTERVAL,
        refetchIntervalInBackground: true,
    });

    return (
        <AppVersionContext.Provider
            value={{
                appVersion: version?.Id,
                isOutdatedVersion,
            }}
        >
            {children}
        </AppVersionContext.Provider>
    );
};

export default AppVersionManager;
